import { useCallback, useMemo } from 'react';

import { patternFormatter } from 'react-number-format';

import { useAuthContext } from '@/_providers/auth-provider';
import type { PhoneNumberQuerySchema } from '@/_schemas/phone-numbers';
import { getSafeNumberParts } from '@/_schemas/phone-numbers';
import { getCallableNumber, useBoolean } from '@/_shared';

import { stringToType } from '../../shared';
import type { PhoneNumberProps } from '../../types';

export type Props = Omit<PhoneNumberProps, 'value'> & {
  index: number;
  value: PhoneNumberQuerySchema[];
  validateDuplicatePhoneNumber?: (phoneNumber: string) => Promise<boolean>;
};

export type AddPhoneNumberRef = {
  number: string;
  validate: () => Promise<boolean>;
};

export function usePreviewNumber({ value, index, onChange }: Props) {
  const currentPhoneNumber = value[index];
  const { number, type: strType, id, country } = currentPhoneNumber;

  const { user } = useAuthContext();

  const edit = useBoolean();

  const type = stringToType(strType);

  const onDelete = useCallback(async () => {
    const nv = value.filter((item) => item.id !== id);

    onChange('DELETE')(nv);
  }, [id, value, onChange]);

  const handleOpen = useCallback(() => {
    if (currentPhoneNumber.number) {
      window.open(`tel:${getCallableNumber(currentPhoneNumber, user)}`, '_SELF');
    }
  }, [currentPhoneNumber, user]);

  const values =
    useMemo(() => {
      const safeParts = getSafeNumberParts(number, country?.calling_code, country?.phone_format);

      return {
        ...safeParts,
        formattedNumber: patternFormatter(safeParts.onlyNumber, { format: safeParts.fullFormat }).trim(),
      };
    }, [country?.calling_code, country?.phone_format, number]) ?? [];

  return { edit, type, number, ...values, country, onDelete, handleOpen };
}
